<template>
  <div class="api-keys-content">
    <section v-if="keys.length" class="api-keys-content__section">
      <div class="api-keys-content__section-header">
        <h2 class="api-keys-content__section-heading">
          Usage Summary
          <BittsTooltip placement="top">
            <FontAwesomeIcon
              :icon="['fas', 'circle-info']"
              class="api-keys-content__stats-info"
            />
            <template #title>
              {{ limitsTooltipText }}
            </template>
          </BittsTooltip>
        </h2>
      </div>
      <BittsContainer class="api-keys-content__stats">
        <div class="api-keys-content__stats-header">
          <div class="api-keys-content__total">
            <span class="api-keys-content__total-value">
              {{ totalUsageCount.toLocaleString() }}
            </span>
            <span class="api-keys-content__total-label">Total Requests</span>
          </div>
          <div class="api-keys-content__refresh">
            <span
              v-if="lastRefreshedLabel"
              class="api-keys-content__refresh-label"
            >
              Last refreshed {{ lastRefreshedLabel }}
            </span>
            <BittsButton
              type="neutral"
              variant="ghost"
              size="x-small"
              :center-icon="['fas', 'arrows-rotate']"
              :loading="isRefreshing"
              :disabled="isRefreshing"
              aria-label="Refresh API keys"
              @click="debouncedRefresh"
            />
          </div>
        </div>

        <div
          v-if="limits"
          class="api-keys-content__daily-usage"
          :title="`${globalDailyUsagePercent}% of daily limit used today`"
        >
          <BittsProgressBar
            :width="`${Math.min(globalDailyUsagePercent, 100)}%`"
            :height="8"
            :color="usageBarColor"
          />
          <span class="api-keys-content__daily-usage-label">
            {{ totalDailyUsageCount.toLocaleString() }} /
            {{ limits.daily_limit.toLocaleString() }} req today
          </span>
        </div>

        <BittsCollapse
          v-model:is-open="isExamplesOpen"
          aria-label="Examples"
          class="api-keys-content__examples"
        >
          <template #title>
            <span class="api-keys-content__examples-title">
              <FontAwesomeIcon :icon="['fas', 'code']" />
              Examples
            </span>
          </template>
          <!-- @click.stop because BittsCollapse toggles on any descendant click -->
          <div class="api-keys-content__examples-body" @click.stop>
            <div
              v-for="example in examples"
              :key="example.language"
              class="api-keys-content__example"
            >
              <div class="api-keys-content__example-header">
                <span class="api-keys-content__example-language">
                  {{ example.language }}
                </span>
                <BittsButton
                  type="neutral"
                  variant="ghost"
                  size="x-small"
                  :center-icon="
                    copiedByLanguage[example.language]
                      ? ['fas', 'check']
                      : ['fak', 'copy']
                  "
                  :aria-label="`Copy ${example.language} example`"
                  @click.stop="copyExample(example.language, example.code)"
                />
              </div>
              <pre
                class="api-keys-content__example-code hljs"
              ><code v-html="renderExample(example.code, example.hljsLanguage)" /></pre>
            </div>
          </div>
        </BittsCollapse>
      </BittsContainer>
    </section>

    <BittsLoading v-if="isLoading" :is-loading="true" />

    <BittsEmptyState
      v-else-if="!keys.length"
      :font-awesome-icon="['fas', 'key']"
      title="No API keys yet"
      description="Generate a personal API key to authenticate requests to the Crossbeam API."
      :button="
        canWrite ? { text: 'Create an API Key', type: 'primary' } : undefined
      "
      @button-clicked="isCreating = true"
    />

    <section v-else class="api-keys-content__section">
      <div class="api-keys-content__section-header">
        <h2 class="api-keys-content__section-heading">Active Keys</h2>
        <BittsTooltip
          v-if="canWrite"
          :show-tooltip="isAtKeyLimit"
          placement="top"
        >
          <BittsButton
            text="Add API Key"
            type="neutral"
            variant="ghost"
            size="small"
            :left-icon="['fas', 'plus']"
            :aria-disabled="isAtKeyLimit"
            data-testid="add-api-key-button"
            @click="isCreating = true"
          />
          <template #title>
            You've reached the limit of {{ MAX_KEYS_PER_ORG }} API keys per
            organization. Revoke an existing key to create a new one.
          </template>
        </BittsTooltip>
      </div>
      <div class="api-keys-content__list">
        <ApiKeyItem
          v-for="apiKey in keys"
          :key="apiKey.id"
          :api-key="apiKey"
          :can-write="canWrite"
          :limits="limits"
          @revoked="handleRevoked"
        />
      </div>
    </section>

    <ApiKeyCreateDrawer
      v-if="isCreating"
      :invalid-key-names="allInvalidKeyNames"
      :available-scopes="availableScopes"
      @created="handleCreated"
      @closed="isCreating = false"
    />
  </div>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsCollapse,
  BittsContainer,
  BittsEmptyState,
  BittsLoading,
  BittsProgressBar,
  BittsTooltip,
} from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { debounce } from 'lodash';
import { DateTime } from 'luxon';
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  shallowRef,
  watch,
} from 'vue';

import ApiKeyCreateDrawer from '@/components/ApiKeyCreateDrawer.vue';
import ApiKeyItem from '@/components/ApiKeyItem.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import appConfig from '@/config';
import { useFlashesStore } from '@/stores';
import type { ApiKey, ApiKeyLimits } from '@/types/api_keys';
import { copyToClipBoard } from '@/utils';
import { usageColorFor } from '@/utils/usageColor';

const MAX_KEYS_PER_ORG = 20;

const { hasScope } = useAuth();
const flashesStore = useFlashesStore();
const canWrite = computed(() => hasScope('write:api-keys'));
const isAtKeyLimit = computed(() => keys.value.length >= MAX_KEYS_PER_ORG);

const keys = ref<ApiKey[]>([]);
const limits = ref<ApiKeyLimits | null>(null);
const invalidKeyNames = ref<string[]>([]);
const availableScopes = ref<string[]>([]);
const isLoading = ref(true);
const isRefreshing = ref(false);
const isCreating = ref(false);
const isExamplesOpen = ref(false);
const lastRefreshedAt = ref<DateTime | null>(null);
const now = ref(DateTime.now());

const totalUsageCount = computed(() => limits.value?.total_usage_count ?? 0);
const totalDailyUsageCount = computed(
  () => limits.value?.daily_usage_count ?? 0,
);

const globalDailyUsagePercent = computed(() => {
  if (!limits.value?.daily_limit) return 0;
  return Math.round(
    (totalDailyUsageCount.value / limits.value.daily_limit) * 100,
  );
});

const usageBarColor = computed(() =>
  usageColorFor(globalDailyUsagePercent.value),
);

const limitsTooltipText = computed(() => {
  const base = `Totals include usage from revoked keys. Deleting a key does not reduce the current period's counters.`;
  if (!limits.value) return base;
  const perMin = limits.value.requests_per_minute.toLocaleString();
  const perDay = limits.value.daily_limit.toLocaleString();
  return `Rate limits: ${perMin} req/min · ${perDay} req/day. ${base}`;
});

const allInvalidKeyNames = computed(() => [
  ...invalidKeyNames.value,
  ...keys.value.map((k) => k.name),
]);

const copiedByLanguage = reactive<Record<string, boolean>>({});
const copyResetTimers: Record<string, ReturnType<typeof setTimeout>> = {};

async function copyExample(language: string, code: string) {
  await copyToClipBoard(code);
  copiedByLanguage[language] = true;
  if (copyResetTimers[language]) clearTimeout(copyResetTimers[language]);
  copyResetTimers[language] = setTimeout(() => {
    copiedByLanguage[language] = false;
  }, 1500);
}

type HljsInstance = {
  highlight(code: string, opts: { language: string }): { value: string };
};
const hljs = shallowRef<HljsInstance | null>(null);
let hljsLoadPromise: Promise<void> | null = null;

function loadHighlighter(): Promise<void> {
  if (hljsLoadPromise) return hljsLoadPromise;
  hljsLoadPromise = (async () => {
    try {
      const [core, bash, python, javascript] = await Promise.all([
        import('highlight.js/lib/core'),
        import('highlight.js/lib/languages/bash'),
        import('highlight.js/lib/languages/python'),
        import('highlight.js/lib/languages/javascript'),
        import('highlight.js/styles/atom-one-light.css'),
      ]);
      const instance = core.default;
      instance.registerLanguage('bash', bash.default);
      instance.registerLanguage('python', python.default);
      instance.registerLanguage('javascript', javascript.default);
      hljs.value = instance as HljsInstance;
    } catch (err) {
      // Clear the cached promise so a subsequent open can retry. The
      // escapeHtml fallback in renderExample keeps the examples readable.
      hljsLoadPromise = null;
      console.warn('Failed to load highlight.js', err);
    }
  })();
  return hljsLoadPromise;
}

watch(isExamplesOpen, (open) => {
  if (open) loadHighlighter();
});

const examples = computed(() => {
  const url = `${appConfig.apiBaseUrl}/v1/populations`;
  return [
    {
      language: 'curl',
      hljsLanguage: 'bash',
      code: `curl "${url}" -H "Authorization: Bearer <your-api-key>"`,
    },
    {
      language: 'httpie',
      hljsLanguage: 'bash',
      code: `http ${url} \\
  Authorization:"Bearer <your-api-key>"`,
    },
    {
      language: 'python',
      hljsLanguage: 'python',
      code: `import requests

r = requests.get(
    "${url}",
    headers={"Authorization": "Bearer <your-api-key>"}
)`,
    },
    {
      language: 'js',
      hljsLanguage: 'javascript',
      code: `const res = await fetch("${url}", {
  headers: { Authorization: "Bearer <your-api-key>" }
});`,
    },
  ];
});

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function renderExample(code: string, hljsLanguage: string): string {
  if (!hljs.value) return escapeHtml(code);
  return hljs.value.highlight(code, { language: hljsLanguage }).value;
}

const lastRefreshedLabel = computed(() => {
  if (!lastRefreshedAt.value || !limits.value) return null;
  const diff = now.value.diff(lastRefreshedAt.value, 'seconds').seconds;
  if (diff < 10) return 'just now';
  const rel = lastRefreshedAt.value.toRelative({ base: now.value });
  if (!rel || rel.startsWith('in')) return 'just now';
  return rel;
});

async function fetchKeys() {
  isRefreshing.value = true;
  const { data, error } = await crossbeamApi.GET('/v0.1/api-keys');
  if (error) {
    flashesStore.addErrorFlash({ message: 'Failed to load API keys' });
  } else if (data) {
    keys.value = data.keys;
    limits.value = data.limits;
    invalidKeyNames.value = data.invalid_key_names;
    availableScopes.value = data.available_scopes;
    lastRefreshedAt.value = DateTime.now();
  }
  isLoading.value = false;
  isRefreshing.value = false;
}

async function handleRefresh() {
  isRefreshing.value = true;
  const { error } = await crossbeamApi.POST('/v0.1/api-keys/flush', {});
  if (error) {
    flashesStore.addErrorFlash({ message: 'Failed to refresh API keys' });
    isRefreshing.value = false;
    return;
  }
  await fetchKeys();
}

const debouncedRefresh = debounce(handleRefresh, 5000, {
  leading: true,
  trailing: false,
});

let tickInterval: ReturnType<typeof setInterval>;

onMounted(() => {
  fetchKeys();
  tickInterval = setInterval(() => {
    now.value = DateTime.now();
  }, 30_000);
});

onUnmounted(() => {
  clearInterval(tickInterval);
  Object.values(copyResetTimers).forEach(clearTimeout);
});

function handleCreated() {
  fetchKeys();
}

function handleRevoked(id: string) {
  const revoked = keys.value.find((k) => k.id === id);
  if (revoked) invalidKeyNames.value = [...invalidKeyNames.value, revoked.name];
  keys.value = keys.value.filter((k) => k.id !== id);
}
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";
.api-keys-content {
  @apply flex flex-col gap-24 pb-32;
}

.api-keys-content__section {
  @apply flex flex-col gap-12;
}

.api-keys-content__section-heading {
  @apply text-base font-bold text-neutral-text-strong m-0
    inline-flex items-center gap-6;
}

.api-keys-content__section-header {
  @apply flex items-center justify-between gap-12;
}

.api-keys-content__stats {
  @apply flex flex-col gap-16 shadow-none;
}

.api-keys-content__stats-header {
  @apply flex flex-wrap items-center gap-16;
}

.api-keys-content__stats-info {
  @apply text-neutral-text-placeholder hover:text-neutral-text-weak transition-colors text-12;
}

.api-keys-content__total {
  @apply flex items-baseline gap-8;
}

.api-keys-content__total-value {
  @apply font-mono text-lg font-bold text-neutral-text-strong tabular-nums;
}

.api-keys-content__total-label {
  @apply text-sm text-neutral-text-weak;
}

.api-keys-content__refresh {
  @apply flex items-center gap-8 ml-auto;
}

.api-keys-content__refresh-label {
  @apply text-xs text-neutral-text-placeholder;
}

.api-keys-content__daily-usage {
  @apply flex flex-col gap-6;
}

.api-keys-content__daily-usage-label {
  @apply text-xs text-neutral-text-weak tabular-nums;
}

.api-keys-content__list {
  @apply grid grid-cols-1 gap-12 items-start;
}

.api-keys-content__list:has(> :nth-child(2)) {
  @apply md:grid-cols-2;
}

.api-keys-content__examples {
  @apply pt-4;
}

.api-keys-content__examples-title {
  @apply inline-flex items-center gap-8 text-sm font-normal text-neutral-text-weak;
}

:deep(.api-keys-content__examples .bitts-collapse__header) {
  @apply font-normal;
}

:deep(.api-keys-content__examples .bitts-collapse__prefix-chevron) {
  @apply text-neutral-text-placeholder! h-12! w-12!;
}

.api-keys-content__examples-body {
  @apply flex flex-col gap-16 pt-8 pr-8;
}

.api-keys-content__example {
  @apply flex flex-col gap-4;
}

.api-keys-content__example-header {
  @apply flex items-center gap-8;
}

.api-keys-content__example-language {
  @apply text-xs font-medium uppercase tracking-wide text-neutral-text-weak;
}

:deep(.api-keys-content__example-code.hljs) {
  @apply bg-neutral-background-weak! px-12 py-8 rounded-lg;
}

.api-keys-content__example-code {
  @apply m-0 font-mono text-xs text-neutral-text-strong whitespace-pre-wrap break-all;
}
</style>
